import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import {
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import {
  useLinkCustomerSecretProviderMutation,
  useListCustomerSecretProviderQuery,
} from '@/graphql/generated/hooks'
import { CredentialLinkEntityType } from '@/graphql/generated/schemas'

import { SecretArnStep } from './SecretArnStep'
import { SelectProviderStep } from './SelectProviderStep'

interface LinkSecretProviderModalIProps {
  isOpen: boolean
  onClose: () => void
  entityId: string
  entityType: CredentialLinkEntityType
  refetchQueryName: string
  title?: string
  secretTemplate?: string
}

export const LinkSecretProviderModal = ({
  isOpen,
  onClose,
  entityId,
  entityType,
  refetchQueryName,
  title,
  secretTemplate,
}: LinkSecretProviderModalIProps) => {
  const [secretArn, setSecretArn] = useState<string | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { showError } = useToasts()

  const { data } = useListCustomerSecretProviderQuery()
  const providers =
    data?.customerSecretProviders?.edges?.map((e) => e?.node) ?? []

  const [linkCustomerSecretProvider] = useLinkCustomerSecretProviderMutation()

  const onLink = async () => {
    if (!selectedProviderId) return
    setIsLoading(true)
    try {
      const result = await linkCustomerSecretProvider({
        variables: {
          input: {
            customerSecretProviderId: selectedProviderId,
            entityId,
            entityType,
          },
        },
        refetchQueries: [refetchQueryName],
      })
      const arn =
        result.data?.linkCustomerSecretProvider?.customerSecretProviderLink
          ?.entity?.customerSecretProvider?.secretArn ?? null
      setSecretArn(arn)
    } catch (error) {
      showError(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setSecretArn(null)
    setSelectedProviderId('')
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          fontSize='xl'
          fontWeight='extrabold'
          letterSpacing='-0.6px'
        >
          {secretArn
            ? 'Secret Associated'
            : title ?? 'Associate Secret Provider'}
        </ModalHeader>
        <ModalBody pb={6} px={6} py={2}>
          {secretArn ? (
            <SecretArnStep
              onClose={handleClose}
              secretArn={secretArn}
              secretTemplate={secretTemplate}
            />
          ) : (
            <SelectProviderStep
              isLoading={isLoading}
              onClose={handleClose}
              onLink={onLink}
              onProviderChange={setSelectedProviderId}
              providers={providers}
              selectedProviderId={selectedProviderId}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
